import { Clock, ExternalLink, Mail, MapPin, Phone } from "lucide-react";
import React from "react";
import Layout from "../../components/layout/Layout";
import { Card, CardContent } from "../../components/ui/card";
import { useLanguage } from "../../context/LanguageContext";

const ContactPage = () => {
  const { t, lang } = useLanguage();

  return (
    <Layout>
      {/* Header */}
      <section className="bg-islamic-teal text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="font-serif text-3xl md:text-4xl font-bold mb-2">
            {t("contact.title")}
          </h1>
          <p className="text-white/70">
            {lang === "ar"
              ? "تواصل معنا"
              : lang === "en"
              ? "Get in touch with us"
              : "Ia legătura cu noi"}
          </p>
        </div>
      </section>

      <section className="py-8 md:py-12 bg-islamic-pearl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Contact Info */}
            <div className="space-y-6">
              <Card className="border-stone-200 shadow-md">
                <CardContent className="p-6">
                  <h2 className="font-serif text-xl font-semibold text-stone-800 mb-6">
                    {lang === "ar"
                      ? "معلومات الاتصال"
                      : lang === "en"
                      ? "Contact Information"
                      : "Informații de contact"}
                  </h2>

                  <div className="space-y-6">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-lg bg-islamic-teal/10 flex items-center justify-center shrink-0">
                        <MapPin className="w-5 h-5 text-islamic-teal" />
                      </div>
                      <div>
                        <h3 className="font-medium text-stone-800 mb-1">
                          {t("contact.address")}
                        </h3>
                        <p className="text-stone-600">
                          Strada Muntii Gurghiului nr 50-56
                          <br />
                          060316 , București 010035
                          <br />
                          România
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-lg bg-islamic-teal/10 flex items-center justify-center shrink-0">
                        <Phone className="w-5 h-5 text-islamic-teal" />
                      </div>
                      <div>
                        <h3 className="font-medium text-stone-800 mb-1">
                          {t("contact.phone")}
                        </h3>
                        <p className="text-stone-600">+40 21 314 5678</p>
                        <p className="text-stone-600">+40 722 123 456</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-lg bg-islamic-teal/10 flex items-center justify-center shrink-0">
                        <Mail className="w-5 h-5 text-islamic-teal" />
                      </div>
                      <div>
                        <h3 className="font-medium text-stone-800 mb-1">
                          {t("contact.email")}
                        </h3>
                        <a
                          href="mailto:contact@musulman.ro"
                          className="text-islamic-teal hover:underline">
                          contact@musulman.ro
                        </a>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-lg bg-islamic-teal/10 flex items-center justify-center shrink-0">
                        <Clock className="w-5 h-5 text-islamic-teal" />
                      </div>
                      <div>
                        <h3 className="font-medium text-stone-800 mb-1">
                          {t("contact.hours")}
                        </h3>
                        <p className="text-stone-600">
                          {t("contact.openDaily")}: 05:00 - 22:00
                        </p>
                        <p className="text-stone-600 mt-2">
                          <span className="font-medium">
                            {t("contact.fridayPrayer")}:
                          </span>{" "}
                          13:30
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Google Maps Link */}
              <a
                href="https://maps.app.goo.gl/F47uj4pD13o6GCrUA"
                target="_blank"
                rel="noopener noreferrer"
                className="block"
                data-testid="google-maps-link">
                <Card className="border-stone-200 shadow-md hover:shadow-lg transition-shadow cursor-pointer">
                  <CardContent className="p-6 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-islamic-gold/10 flex items-center justify-center">
                        <MapPin className="w-5 h-5 text-islamic-gold" />
                      </div>
                      <span className="font-medium text-stone-800">
                        {lang === "ar"
                          ? "افتح في خرائط جوجل"
                          : lang === "en"
                          ? "Open in Google Maps"
                          : "Deschide în Google Maps"}
                      </span>
                    </div>
                    <ExternalLink className="w-5 h-5 text-stone-400" />
                  </CardContent>
                </Card>
              </a>
            </div>

            {/* Map */}
            <div>
              <Card className="border-stone-200 shadow-md overflow-hidden h-full min-h-[400px]">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2848.262418820378!2d26.046248499999997!3d44.44829!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x40b20193f9c65281%3A0x8042e5e6e12b614c!2sMasjid%20Al%20Rahman!5e0!3m2!1sfr!2sfr!4v1775954584123!5m2!1sfr!2sfr"
                  width="100%"
                  height="100%"
                  style={{ border: 0, minHeight: "400px" }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Mosque Location"
                  data-testid="google-map"></iframe>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default ContactPage;
